import { useCallback, useEffect, useMemo, useState, type CSSProperties, type ReactNode } from 'react';
import { color, font, stageTheme } from '../styles/theme';
import { shortDate, fullDate } from '../lib/format';
import { makeLookup, registryFieldsOf, summaryOf, type Lookup } from '../lib/presenters';
import { cellOf, columnsFor, gridFor, minWidthFor } from '../lib/columns';
import { dueFromArrival, dueFromClock, dueLabel, replyDays, type Due } from '../lib/deadlines';
import { useRemembered } from '../lib/remembered';
import {
  MESSAGE_GROUPS,
  MESSAGE_GUIDE,
  groupOf,
  messageForms,
  messageName,
  messageOne,
  messageStatusOptions,
  offeredOnSite,
  siteLink,
} from '../lib/messageTypes';
import { useMeta, useOverview, useSubmissions, useSetStatus } from '../api/queries';
import { useReplyClocks, type Ticket } from '../api/operations';
import { downloadCsv, type InboxCounts, type MetaResponse, type SubmissionRow } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import {
  PageTitle,
  Card,
  Chip,
  ColHead,
  FormPill,
  Loading,
  ErrorNote,
  RedactedBadge,
  RoundButton,
  selectStyle,
} from '../components/ui';
import { IconDownload, IconMail, IconExternal, IconCaret } from '../components/icons';
import { SubmissionDetail } from './SubmissionDetail';
import { Inbox } from './Inbox';
import { useInboxCounts } from '../api/inbox';

/**
 * Messages — everything people send the lab that is not a step on the
 * participant path.
 *
 * The list on the left is the navigation: every kind of message, grouped by
 * who sends it, with what the sender wants under each name. "Needs attention"
 * sits above them all — whatever is past its reply deadline or due today —
 * because a queue sorted by type still hides the one thing that is late.
 *
 * Each type opens with one line saying who answers it, how fast and what
 * finishes it. The rest (what it is and is not, where on the site it comes
 * from) is a panel behind "About this type", open until someone closes it and
 * closed from then on: it teaches in the first week and stays out of the way
 * after that.
 *
 * One vocabulary: the filters, the rows and the finishing step all use the
 * message statuses — Received, Under review, Hold, Done, Stopped. The due date
 * is the API's reply clock where one is open, and otherwise worked out from
 * the type's reply time (lib/deadlines.ts).
 *
 * Both form groups arrive in two requests, 100 rows each, and every filter
 * after that is in the browser. The search is the one at the top of the page;
 * the table says what it is searching.
 */

/** `all`, `attention`, or one message form's key. */
export type MessageView = string;

const ATTENTION = 'attention';
/** The enact@ inbox (API 051): mail that came with no form. */
export const INBOX = 'inbox';
const FETCH_SIZE = 100;
const PAGE_SIZE = 10;
/** Where the About panel's open or closed state is remembered, per browser. */
const ABOUT_KEY = 'enact.messages.about';

type Sort = 'newest' | 'oldest' | 'name' | 'due';

/** Statuses in which a message is waiting on the lab. */
const AWAITING: readonly string[] = ['received', 'under-review'];

/**
 * Whether the lab still owes this message something. A stop-contact request
 * arrives already Stopped — the address is suppressed on arrival — but
 * someone still has to check the other contacts it lists and mark it
 * processed, so it counts until it is Done.
 */
const awaiting = (r: SubmissionRow): boolean =>
  r.form === 'stop-contacting-me' ? r.status !== 'done' : AWAITING.includes(r.status);

const topicOf = (r: SubmissionRow): string | undefined => (r.data as { topic?: string }).topic;

/** Two lines, then an ellipsis: room for the question without a row twice as tall. */
const CLAMP: CSSProperties = {
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  fontSize: 13,
  lineHeight: '18px',
  color: color.ink,
  minWidth: 0,
};

export function Messages({
  view,
  onView,
  search,
  onSearch,
  inboxThread,
  onInboxThread,
}: {
  view: MessageView;
  onView: (next: MessageView) => void;
  search: string;
  onSearch: (q: string) => void;
  /** The inbox conversation open, if any — the bell can open one. */
  inboxThread: string | null;
  onInboxThread: (id: string | null) => void;
}) {
  const meta = useMeta();
  const inboxCounts = useInboxCounts(true);
  const overview = useOverview();
  const { can } = useAuth();
  const setStatus = useSetStatus();
  const clocks = useReplyClocks();

  const [status, setStatusFilter] = useState<string>('all');
  const [topic, setTopic] = useState<string>('all');
  const [sort, setSort] = useState<Sort>(view === ATTENTION ? 'due' : 'newest');
  const [page, setPage] = useState(1);
  // The envelope opens the same record with the composer already focused.
  const [selected, setSelected] = useState<{ id: string; focus?: 'reply' } | null>(null);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [aboutOpen, setAboutOpen] = useRemembered(ABOUT_KEY, true);

  // Page 3 of the old list means nothing against a new one.
  useEffect(() => setPage(1), [view, search, status, topic, sort]);
  // A new type starts unfiltered; the attention list reads soonest first.
  useEffect(() => {
    setStatusFilter('all');
    if (view !== 'inquiry') setTopic('all');
    if (view === ATTENTION) setSort('due');
  }, [view]);

  const base = { status: 'all', q: search, sort: 'newest' as const, page: 1, pageSize: FETCH_SIZE };
  const inquiries = useSubmissions({ ...base, tab: 'inquiry' });
  const requests = useSubmissions({ ...base, tab: 'requests' });
  const close = useCallback(() => setSelected(null), []);
  const L = useMemo(() => (meta.data ? makeLookup(meta.data) : null), [meta.data]);

  const rows = useMemo(
    () => [...(inquiries.data?.rows ?? []), ...(requests.data?.rows ?? [])],
    [inquiries.data, requests.data],
  );

  /** New per type — the same `fresh` counts the sidebar badge adds up. */
  const fresh = useMemo(
    () => new Map((overview.data?.formStats ?? []).map((f) => [f.key as string, f.fresh])),
    [overview.data],
  );

  if (meta.isLoading) return <Loading />;
  if (!meta.data || !L) return <ErrorNote message="Could not load the workflow configuration." />;
  const m = meta.data;

  // A type the site does not offer stays out of the list until one arrives.
  const forms = messageForms(m).filter((k) => offeredOnSite(k) || rows.some((r) => r.form === k));
  const current =
    view === ATTENTION || view === INBOX ? view : view !== 'all' && forms.includes(view) ? view : 'all';

  const now = new Date();
  const dues = new Map<string, Due | null>();
  for (const r of rows) {
    let due: Due | null = null;
    if (awaiting(r)) {
      const clock = clocks.data?.get(r.id);
      if (clock?.dueAt) due = dueFromClock(clock.dueAt, now);
      else {
        const days = replyDays(m.forms[r.form]?.sla);
        if (days !== null) due = dueFromArrival(r.createdAt, days, now);
      }
    }
    dues.set(r.id, due);
  }
  const dueOf = (r: SubmissionRow) => dues.get(r.id) ?? null;
  const needsAttention = (r: SubmissionRow) => {
    const d = dueOf(r);
    return !!d && d.state !== 'upcoming';
  };

  const attentionRows = rows.filter(needsAttention);
  const lateBy = new Map<string, number>();
  const matches = new Map<string, number>();
  for (const r of rows) {
    matches.set(r.form, (matches.get(r.form) ?? 0) + 1);
    if (dueOf(r)?.state === 'overdue') lateBy.set(r.form, (lateBy.get(r.form) ?? 0) + 1);
  }
  const freshTotal = forms.reduce((n, k) => n + (fresh.get(k) ?? 0), 0);

  const inView =
    current === 'all'
      ? rows
      : current === ATTENTION
        ? attentionRows
        : rows.filter((r) => r.form === current);
  const inTopic =
    current === 'inquiry' && topic !== 'all' ? inView.filter((r) => topicOf(r) === topic) : inView;
  const statusCounts = new Map<string, number>();
  for (const r of inTopic) statusCounts.set(r.status, (statusCounts.get(r.status) ?? 0) + 1);
  const filtered = status === 'all' ? inTopic : inTopic.filter((r) => r.status === status);

  const newest = (a: SubmissionRow, b: SubmissionRow) =>
    a.createdAt < b.createdAt ? 1 : a.createdAt > b.createdAt ? -1 : 0;
  const ORDER: Record<Sort, (a: SubmissionRow, b: SubmissionRow) => number> = {
    newest,
    oldest: (a, b) => -newest(a, b),
    name: (a, b) => a.name.localeCompare(b.name),
    // Soonest deadline first; anything without one after, newest first.
    due: (a, b) => {
      const da = dueOf(a)?.date;
      const db = dueOf(b)?.date;
      if (da && db && da !== db) return da < db ? -1 : 1;
      if (da && !db) return -1;
      if (!da && db) return 1;
      return newest(a, b);
    },
  };
  const shown = [...filtered].sort(ORDER[sort]);

  const pageMax = Math.max(1, Math.ceil(shown.length / PAGE_SIZE));
  const pageNow = Math.min(page, pageMax);
  const pageRows = shown.slice((pageNow - 1) * PAGE_SIZE, pageNow * PAGE_SIZE);

  const lists = [inquiries, requests];
  const loading = lists.some((l) => l.isLoading && !l.data);
  const failed = lists.find((l) => l.error);
  const truncated = lists.some((l) => l.data && l.data.total > l.data.rows.length);
  const canUpdate = can('submissions:update');

  // A type with its own column set shows it; the rest share Type · About or,
  // for general inquiries, Topic · Message.
  const single = current !== 'all' && current !== ATTENTION;
  const typeCols = single && current !== 'inquiry' ? columnsFor(current) : null;
  const grid = gridFor(typeCols);
  const guide = single ? MESSAGE_GUIDE[current] : undefined;
  const scopeName =
    current === 'all' ? 'all messages' : current === ATTENTION ? 'Needs attention' : messageName(m, current);

  async function exportCsv() {
    setExporting(true);
    setExportError(null);
    try {
      // The whole type: on a form tab the list's `status` is a status key,
      // and the export is meant to be the type, not the filter on screen.
      await downloadCsv(
        '/submissions/export.csv',
        { tab: current, status: 'all', q: search, sort: 'newest' },
        `enact-${current}.csv`,
      );
    } catch (err) {
      setExportError(err instanceof Error ? err.message : 'Export failed.');
    } finally {
      setExporting(false);
    }
  }

  function empty(): string {
    if (search) return `Nothing in ${scopeName} matches “${search}”.`;
    if (current === ATTENTION) {
      return 'Nothing needs attention: every message still waiting on the lab is inside its reply time.';
    }
    if (status !== 'all') {
      const label = m.statuses.find((s) => s.key === status)?.label ?? status;
      return `Nothing here is ${label.toLowerCase()}.`;
    }
    if (guide && guide.from.length === 0) {
      return 'Nothing yet. The public site does not offer this form, so nothing arrives here until it does.';
    }
    return 'Nothing here yet.';
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }} className="fade-in">
      <PageTitle sub="Questions and requests people send the lab, other than asking to join a study.">
        Messages
      </PageTitle>

      <div className="msg-layout">
        <TypeRail
          meta={m}
          forms={forms}
          current={current}
          onView={onView}
          attention={attentionRows.length}
          freshTotal={freshTotal}
          fresh={fresh}
          lateBy={lateBy}
          matches={matches}
          searching={!!search}
          total={rows.length}
          inbox={inboxCounts.data}
        />

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, minWidth: 0 }}>
          <TypePicker meta={m} forms={forms} current={current} onView={onView} attention={attentionRows.length} />

          {current === INBOX ? (
            <Inbox openId={inboxThread} onOpen={onInboxThread} />
          ) : (
          <>
          <TypeHeader
            meta={m}
            form={current}
            attention={attentionRows.length}
            aboutOpen={aboutOpen}
            onAbout={setAboutOpen}
          />

          {exportError && <ErrorNote message={exportError} />}
          {truncated && (
            <ErrorNote message={`Showing the newest ${FETCH_SIZE} of each group. Search to find older messages.`} />
          )}

          <Card pad={0} style={{ overflow: 'hidden' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                flexWrap: 'wrap',
                padding: '12px 20px',
                borderBottom: `1px solid ${color.line}`,
              }}
            >
              <Chip
                label={<Counted text="All" count={inTopic.length} active={status === 'all'} />}
                active={status === 'all'}
                onClick={() => setStatusFilter('all')}
              />
              {messageStatusOptions(m.statuses).map((s) => (
                  <Chip
                    key={s.key}
                    label={<Counted text={s.label} count={statusCounts.get(s.key) ?? 0} active={status === s.key} />}
                    active={status === s.key}
                    onClick={() => setStatusFilter(status === s.key ? 'all' : s.key)}
                  />
                ))}

              <span style={{ flex: 1 }} />

              {single && inView.length > 0 && can('submissions:export') && (
                <button
                  onClick={() => void exportCsv()}
                  disabled={exporting}
                  className="hov-tint"
                  title={`Download every one of these ${scopeName.toLowerCase()} as a spreadsheet`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 7,
                    background: 'transparent',
                    border: 'none',
                    color: color.blue,
                    fontSize: 13,
                    fontWeight: 700,
                    cursor: exporting ? 'not-allowed' : 'pointer',
                    padding: '8px 10px',
                    borderRadius: 10,
                  }}
                >
                  <IconDownload /> {exporting ? 'Preparing…' : 'Export'}
                </button>
              )}

              <label
                style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 13, color: color.muted, fontWeight: 600 }}
              >
                Sort:
                <select
                  value={sort}
                  onChange={(e) => setSort(e.target.value as Sort)}
                  style={{
                    ...selectStyle,
                    width: 'auto',
                    borderRadius: 999,
                    padding: '7px 26px 7px 14px',
                    fontSize: 13,
                  }}
                >
                  <option value="due">due soonest</option>
                  <option value="newest">newest</option>
                  <option value="oldest">oldest</option>
                  <option value="name">name</option>
                </select>
              </label>
            </div>

            {search && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 14,
                  flexWrap: 'wrap',
                  padding: '10px 20px',
                  background: color.tint,
                  borderBottom: `1px solid ${color.line}`,
                  fontSize: 13,
                  color: color.blueDeep,
                }}
              >
                <span>
                  Showing matches for <strong>“{search}”</strong> in <strong>{scopeName}</strong>.
                </span>
                {current !== 'all' && rows.length > inView.length && (
                  <button onClick={() => onView('all')} className="hov-underline" style={linkButton}>
                    Search all messages ({rows.length})
                  </button>
                )}
                <button onClick={() => onSearch('')} className="hov-underline" style={linkButton}>
                  Clear search
                </button>
              </div>
            )}

            {current === 'inquiry' && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  flexWrap: 'wrap',
                  padding: '12px 20px',
                  borderBottom: `1px solid ${color.line}`,
                }}
              >
                <span style={{ fontSize: 12.5, fontWeight: 700, color: color.muted }}>Topic they picked:</span>
                <Chip
                  label={<Counted text="Any" count={inView.length} active={topic === 'all'} />}
                  active={topic === 'all'}
                  onClick={() => setTopic('all')}
                />
                {m.topics.map((t) => (
                  <Chip
                    key={t.key}
                    label={
                      <Counted
                        text={t.label}
                        count={inView.filter((r) => topicOf(r) === t.key).length}
                        active={topic === t.key}
                      />
                    }
                    active={topic === t.key}
                    onClick={() => setTopic(topic === t.key ? 'all' : t.key)}
                  />
                ))}
              </div>
            )}

            <div className="x-scroll">
              <div style={{ minWidth: minWidthFor(typeCols) }}>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: grid,
                    gap: 12,
                    alignItems: 'center',
                    padding: '11px 20px',
                    background: color.bg,
                  }}
                >
                  <ColHead>From</ColHead>
                  {typeCols ? (
                    typeCols.map((c) => <ColHead key={c.key}>{c.label}</ColHead>)
                  ) : current === 'inquiry' ? (
                    <>
                      <ColHead>Topic</ColHead>
                      <ColHead>Message</ColHead>
                    </>
                  ) : (
                    <>
                      <ColHead>Type</ColHead>
                      <ColHead>About</ColHead>
                    </>
                  )}
                  <ColHead>Received</ColHead>
                  <ColHead>Due</ColHead>
                  <ColHead>Status</ColHead>
                  <span />
                </div>

                {loading ? (
                  <Loading />
                ) : failed ? (
                  <ErrorNote message={(failed.error as Error).message} />
                ) : pageRows.length === 0 ? (
                  <p style={{ padding: '28px 20px', fontSize: 14, color: color.muted, margin: 0 }}>{empty()}</p>
                ) : (
                  pageRows.map((r) => {
                    // "May we email a reply" is yes almost every time; the
                    // exception is flagged beside the name, and the envelope
                    // is disabled with it.
                    const noEmail = registryFieldsOf(r)?.['fit.reply_ok'] === false;
                    const noReply = !!MESSAGE_GUIDE[r.form]?.noReply;
                    const canMail = canUpdate && !r.redacted && !!r.email && !noEmail && !noReply;
                    return (
                      <div
                        key={r.id}
                        className="hov-row"
                        style={{
                          display: 'grid',
                          gridTemplateColumns: grid,
                          gap: 12,
                          alignItems: 'center',
                          padding: '12px 20px',
                          borderBottom: `1px solid ${color.line}`,
                        }}
                      >
                        <button
                          onClick={() => setSelected({ id: r.id })}
                          style={{
                            background: 'transparent',
                            border: 'none',
                            padding: 0,
                            cursor: 'pointer',
                            textAlign: 'left',
                            minWidth: 0,
                          }}
                        >
                          <span
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 6,
                              fontSize: 13.5,
                              fontWeight: 700,
                              color: color.ink,
                              minWidth: 0,
                            }}
                          >
                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {r.name || 'No name given'}
                            </span>
                            {r.redacted && <RedactedBadge />}
                            {noEmail && <Flag>No email reply</Flag>}
                          </span>
                          <span
                            style={{
                              display: 'block',
                              fontSize: 11.5,
                              color: color.muted,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {r.redacted ? 'hidden' : r.email || 'No email given'}
                          </span>
                        </button>

                        <RowCells r={r} meta={m} L={L} current={current} cols={typeCols} />

                        <span title={fullDate(r.createdAt)} style={{ fontSize: 12.5, color: color.muted, whiteSpace: 'nowrap' }}>
                          {shortDate(r.createdAt)}
                        </span>

                        <DueCell due={dueOf(r)} row={r} clock={clocks.data?.get(r.id)} sla={m.forms[r.form]?.sla} />

                        <span style={{ minWidth: 0 }}>
                          <select
                            value={r.status}
                            disabled={!canUpdate || setStatus.isPending}
                            aria-label={`Status of the message from ${r.name}`}
                            onChange={(e) => setStatus.mutate({ id: r.id, status: e.target.value })}
                            style={{
                              ...selectStyle,
                              border: 'none',
                              borderRadius: 999,
                              padding: '6px 24px 6px 13px',
                              fontSize: 11.5,
                              fontWeight: 800,
                              background: stageTheme[r.stage].bg,
                              color: stageTheme[r.stage].fg,
                              backgroundImage: selectStyle.backgroundImage,
                              backgroundRepeat: 'no-repeat',
                              backgroundPosition: 'right 9px center',
                              cursor: canUpdate ? 'pointer' : 'not-allowed',
                              maxWidth: '100%',
                            }}
                          >
                            {messageStatusOptions(m.statuses, r.status).map((s) => (
                              <option key={s.key} value={s.key}>
                                {s.label}
                              </option>
                            ))}
                          </select>
                          {/* Who has it, as set from the message's footer. */}
                          {r.ownerName && <OwnerLine name={r.ownerName} />}
                        </span>

                        <span style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          {canUpdate && !r.redacted && r.email && (
                            <button
                              onClick={() => canMail && setSelected({ id: r.id, focus: 'reply' })}
                              disabled={!canMail}
                              title={
                                noReply
                                  ? 'Do not reply by email: they asked us to stop using that address'
                                  : noEmail
                                    ? 'They asked not to be emailed a reply'
                                    : `Reply to ${r.name} by email`
                              }
                              aria-label={canMail ? `Reply to ${r.name} by email` : 'Email reply not allowed'}
                              className={canMail ? 'hov-tint2' : undefined}
                              style={{
                                display: 'flex',
                                background: 'transparent',
                                border: 'none',
                                color: color.muted,
                                cursor: canMail ? 'pointer' : 'not-allowed',
                                opacity: canMail ? 1 : 0.35,
                                padding: '5px 6px',
                                borderRadius: 8,
                              }}
                            >
                              <IconMail />
                            </button>
                          )}
                          <button
                            onClick={() => setSelected({ id: r.id })}
                            title="Open the message"
                            aria-label={`Open the message from ${r.name}`}
                            className="hov-tint2"
                            style={{
                              background: 'transparent',
                              border: 'none',
                              color: color.muted,
                              fontSize: 17,
                              fontWeight: 800,
                              cursor: 'pointer',
                              padding: '4px 8px',
                              borderRadius: 8,
                              lineHeight: 1,
                            }}
                          >
                            ···
                          </button>
                        </span>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 20px' }}>
              <span style={{ fontSize: 12.5, color: color.muted }}>
                <strong style={{ color: color.ink }}>{shown.length}</strong> {shown.length === 1 ? 'message' : 'messages'}
              </span>
              <span style={{ flex: 1 }} />
              <span style={{ fontSize: 12.5, color: color.muted, fontVariantNumeric: 'tabular-nums' }}>
                Page {pageNow} of {pageMax}
              </span>
              <RoundButton title="Previous page" disabled={pageNow <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                ‹
              </RoundButton>
              <RoundButton
                title="Next page"
                disabled={pageNow >= pageMax}
                onClick={() => setPage((p) => Math.min(pageMax, p + 1))}
              >
                ›
              </RoundButton>
            </div>
          </Card>

          {setStatus.error && <ErrorNote message={(setStatus.error as Error).message} />}
          </>
          )}
        </div>
      </div>

      {selected && <SubmissionDetail id={selected.id} focus={selected.focus} onClose={close} />}
    </div>
  );
}

const linkButton: CSSProperties = {
  background: 'transparent',
  border: 'none',
  padding: 0,
  color: color.blue,
  fontSize: 13,
  fontWeight: 700,
  cursor: 'pointer',
};

/** The record's owner, under its status: set from the footer of the open message. */
function OwnerLine({ name }: { name: string }) {
  return (
    <span
      title={`Owner: ${name}`}
      style={{
        display: 'block',
        fontSize: 11,
        color: color.muted,
        margin: '4px 0 0 4px',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
    >
      Owner: {name}
    </span>
  );
}

/** A small grey marker beside a name, for the exception a column used to spell out. */
function Flag({ children }: { children: ReactNode }) {
  return (
    <span
      style={{
        flex: 'none',
        background: color.line,
        color: color.ink,
        fontSize: 10.5,
        fontWeight: 800,
        padding: '2px 8px',
        borderRadius: 999,
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </span>
  );
}

/**
 * When the message is due. Late and due-today are the only states that
 * stand out; a date comfortably ahead is plain text, and a message not
 * waiting on the lab has no deadline at all.
 */
function DueCell({ due, row, clock, sla }: { due: Due | null; row: SubmissionRow; clock?: Ticket; sla?: string }) {
  if (!due) {
    const why = awaiting(row)
      ? 'No reply deadline for this type'
      : row.status === 'hold'
        ? 'On hold: waiting on them, so no clock runs'
        : 'Finished: nothing is due';
    return (
      <span title={why} style={{ fontSize: 12.5, color: color.muted }}>
        —
      </span>
    );
  }

  const source = due.estimated
    ? `Estimated from when it arrived and “${sla ?? 'its reply time'}”: no reply clock is open for it.`
    : `${clock?.kindLabel ?? 'Reply'} clock, as Operations shows it: due 5 pm on ${dueLabel(due.date)}.`;
  const date = `${dueLabel(due.date)}${due.estimated ? ' (est.)' : ''}`;

  if (due.state === 'upcoming') {
    return (
      <span title={source} style={{ fontSize: 12.5, color: color.ink, whiteSpace: 'nowrap' }}>
        {date}
      </span>
    );
  }

  const late = due.state === 'overdue';
  return (
    <span title={source} style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
      <span
        style={{
          alignSelf: 'flex-start',
          background: late ? '#fdf1ee' : stageTheme.active.bg,
          color: late ? color.red : stageTheme.active.fg,
          fontSize: 11,
          fontWeight: 800,
          padding: '2px 8px',
          borderRadius: 999,
          whiteSpace: 'nowrap',
        }}
      >
        {late ? 'Overdue' : 'Due today'}
      </span>
      <span style={{ fontSize: 11.5, color: color.muted, whiteSpace: 'nowrap' }}>{date}</span>
    </span>
  );
}

/** The cells between From and Received, shaped by the type on screen. */
function RowCells({
  r,
  meta,
  L,
  current,
  cols,
}: {
  r: SubmissionRow;
  meta: MetaResponse;
  L: Lookup;
  current: string;
  cols: ReturnType<typeof columnsFor>;
}) {
  if (cols) {
    return (
      <>
        {cols.map((c) => {
          const v = cellOf(r, c, L);
          return (
            <span key={c.key} style={CLAMP} title={v}>
              {v}
            </span>
          );
        })}
      </>
    );
  }

  if (current === 'inquiry') {
    const d = r.data as { topic?: string; organization?: string; message?: string };
    const message = [d.organization, d.message].filter(Boolean).join(' — ');
    return (
      <>
        <span style={{ ...CLAMP, fontSize: 12.5, fontWeight: 700 }} title={L.topicLabel(d.topic)}>
          {L.topicLabel(d.topic)}
        </span>
        <span style={CLAMP} title={message}>
          {message || '—'}
        </span>
      </>
    );
  }

  return (
    <>
      <span style={{ minWidth: 0, overflow: 'hidden' }} title={MESSAGE_GUIDE[r.form]?.what}>
        <FormPill form={r.form} label={messageOne(meta, r.form)} />
      </span>
      <span style={CLAMP} title={summaryOf(r, L)}>
        {summaryOf(r, L)}
      </span>
    </>
  );
}

/** A filter label that carries how many rows sit behind it. */
function Counted({ text, count, active }: { text: string; count: number; active: boolean }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      {text}
      <span
        style={{
          fontSize: 11,
          fontWeight: 800,
          fontVariantNumeric: 'tabular-nums',
          color: active ? 'rgba(255,255,255,0.75)' : color.muted,
        }}
      >
        {count}
      </span>
    </span>
  );
}

const lower = (s: string) => s.charAt(0).toLowerCase() + s.slice(1);

/**
 * Every kind of message, grouped by who sends it — the page's navigation and
 * its legend at once. Only lateness is a filled badge; how many are new is
 * plain text, so the list does not shout about work that is merely unread.
 */
function TypeRail({
  meta,
  forms,
  current,
  onView,
  attention,
  freshTotal,
  fresh,
  lateBy,
  matches,
  searching,
  total,
  inbox,
}: {
  meta: MetaResponse;
  forms: string[];
  current: string;
  onView: (next: MessageView) => void;
  attention: number;
  freshTotal: number;
  fresh: Map<string, number>;
  lateBy: Map<string, number>;
  matches: Map<string, number>;
  searching: boolean;
  total: number;
  inbox?: InboxCounts;
}) {
  const note = (k: string) => (searching ? `${matches.get(k) ?? 0} found` : fresh.get(k) ? `${fresh.get(k)} new` : '');

  return (
    <nav className="msg-rail" aria-label="Message types">
      <Card pad={10} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <RailItem
          name="Needs attention"
          hint="Overdue or due today"
          active={current === ATTENTION}
          badge={attention > 0 ? attention : undefined}
          badgeTitle={`${attention} overdue or due today`}
          note={attention > 0 ? '' : 'All on time'}
          onClick={() => onView(ATTENTION)}
        />
        <RailItem
          name="All messages"
          hint="Every type together"
          active={current === 'all'}
          note={searching ? `${total} found` : freshTotal ? `${freshTotal} new` : ''}
          onClick={() => onView('all')}
        />
        <RailItem
          name="Email inbox"
          hint={inbox?.mailbox ?? 'Mail sent without a form'}
          active={current === INBOX}
          badge={inbox?.unread ? inbox.unread : undefined}
          badgeTitle={`${inbox?.unread ?? 0} unread`}
          note={inbox ? `${inbox.open} open` : ''}
          onClick={() => onView(INBOX)}
        />
        {MESSAGE_GROUPS.map((g) => {
          const members = forms.filter((k) => groupOf(k) === g.key);
          if (members.length === 0) return null;
          return (
            <div key={g.key} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <p
                style={{
                  fontSize: 10.5,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  fontWeight: 800,
                  color: color.muted,
                  margin: '10px 13px 2px',
                }}
              >
                From {lower(g.label)}
              </p>
              {members.map((k) => {
                const late = searching ? 0 : (lateBy.get(k) ?? 0);
                return (
                  <RailItem
                    key={k}
                    name={messageName(meta, k)}
                    hint={MESSAGE_GUIDE[k]?.hint ?? ''}
                    active={current === k}
                    badge={late || undefined}
                    badgeTitle={`${late} overdue`}
                    note={note(k)}
                    onClick={() => onView(k)}
                  />
                );
              })}
            </div>
          );
        })}
      </Card>
    </nav>
  );
}

function RailItem({
  name,
  hint,
  active,
  badge,
  badgeTitle,
  note,
  onClick,
}: {
  name: string;
  hint: string;
  active: boolean;
  /** The one filled marker: work that is late. */
  badge?: number;
  badgeTitle?: string;
  /** Muted, beside the name: how many are new, or found. */
  note: string;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      aria-current={active ? 'page' : undefined}
      className={active ? undefined : 'hov-tint'}
      style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(0,1fr) auto',
        columnGap: 10,
        alignItems: 'center',
        width: '100%',
        textAlign: 'left',
        padding: '7px 10px',
        borderRadius: 12,
        border: 'none',
        borderLeft: `3px solid ${active ? color.blue : 'transparent'}`,
        background: active ? color.tint2 : 'transparent',
        cursor: 'pointer',
      }}
    >
      <span
        style={{
          fontSize: 13.5,
          fontWeight: active ? 800 : 700,
          color: active ? color.navy : color.ink,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {name}
      </span>
      {badge ? (
        <span
          title={badgeTitle}
          style={{
            background: color.red,
            color: color.white,
            fontSize: 11,
            fontWeight: 800,
            minWidth: 20,
            height: 20,
            borderRadius: 999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '0 6px',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {badge}
        </span>
      ) : (
        <span style={{ fontSize: 11.5, fontWeight: 700, color: color.muted, whiteSpace: 'nowrap' }}>{note}</span>
      )}
      {hint && (
        <span style={{ gridColumn: '1 / 3', fontSize: 11.5, lineHeight: '15px', color: color.muted, fontWeight: 500 }}>
          {hint}
        </span>
      )}
    </button>
  );
}

/** The list as one select, for a window too narrow to sit it beside the table. */
function TypePicker({
  meta,
  forms,
  current,
  onView,
  attention,
}: {
  meta: MetaResponse;
  forms: string[];
  current: string;
  onView: (next: MessageView) => void;
  attention: number;
}) {
  return (
    <label className="msg-picker" style={{ alignItems: 'center', gap: 10, fontSize: 13, fontWeight: 700, color: color.muted }}>
      Type:
      <select
        value={current}
        onChange={(e) => onView(e.target.value)}
        style={{ ...selectStyle, width: 'auto', minWidth: 260, fontSize: 13.5 }}
      >
        <option value={ATTENTION}>Needs attention{attention ? ` (${attention})` : ''}</option>
        <option value="all">All messages</option>
        <option value={INBOX}>Email inbox</option>
        {MESSAGE_GROUPS.map((g) => {
          const members = forms.filter((k) => groupOf(k) === g.key);
          if (members.length === 0) return null;
          return (
            <optgroup key={g.key} label={`From ${lower(g.label)}`}>
              {members.map((k) => (
                <option key={k} value={k}>
                  {messageName(meta, k)}
                </option>
              ))}
            </optgroup>
          );
        })}
      </select>
    </label>
  );
}

/**
 * One line on the type: who answers, how fast, what finishes it. Everything
 * else about it waits behind "About this type".
 */
function TypeHeader({
  meta,
  form,
  attention,
  aboutOpen,
  onAbout,
}: {
  meta: MetaResponse;
  form: string;
  attention: number;
  aboutOpen: boolean;
  onAbout: (open: boolean) => void;
}) {
  if (form === 'all' || form === ATTENTION) {
    const all = form === 'all';
    return (
      <Card pad="16px 22px">
        <h2 style={titleStyle}>{all ? 'All messages' : 'Needs attention'}</h2>
        <p style={{ fontSize: 13.5, lineHeight: '20px', color: color.ink, margin: '4px 0 0' }}>
          {all
            ? 'Every type together. The list on the left groups them by who sends them.'
            : `Messages past their reply deadline or due today, soonest first${
                attention ? '' : ' (none right now)'
              }. A stop-contact request stays here until someone marks it processed.`}
        </p>
      </Card>
    );
  }

  const g = MESSAGE_GUIDE[form];
  const fm = meta.forms[form as keyof MetaResponse['forms']];
  const group = MESSAGE_GROUPS.find((x) => x.key === groupOf(form));
  const done = meta.statuses.find((s) => s.key === fm?.primary.to)?.label ?? 'Done';

  return (
    <Card pad="16px 22px">
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 240 }}>
          <p style={eyebrowStyle}>From {group ? lower(group.label) : 'anyone'}</p>
          <h2 style={{ ...titleStyle, margin: '2px 0 0' }}>{messageName(meta, form)}</h2>
        </div>
        <button
          onClick={() => onAbout(!aboutOpen)}
          aria-expanded={aboutOpen}
          className="hov-tint"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 7,
            background: 'transparent',
            border: `1px solid ${color.line}`,
            borderRadius: 999,
            padding: '6px 14px',
            fontSize: 12.5,
            fontWeight: 700,
            color: color.blue,
            cursor: 'pointer',
          }}
        >
          About this type
          <span style={{ display: 'flex', transform: aboutOpen ? 'rotate(180deg)' : undefined }}>
            <IconCaret size={11} />
          </span>
        </button>
      </div>

      {fm && (
        <p style={{ fontSize: 13, lineHeight: '19px', color: color.ink, margin: '8px 0 0' }}>
          <Label>Answered by</Label> {fm.route}
          <Sep />
          <Label>Reply</Label> {fm.sla ? lower(fm.sla) : 'no set time'}
          <Sep />
          <Label>Finish</Label> “{fm.primary.label}”, which sets it to {done}
          {g?.noReply && (
            <>
              <Sep />
              <strong style={{ fontWeight: 800, color: color.red }}>Do not reply by email</strong>
            </>
          )}
        </p>
      )}

      {aboutOpen && (
        <div
          style={{
            marginTop: 12,
            paddingTop: 12,
            borderTop: `1px solid ${color.line}`,
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            maxWidth: 860,
          }}
        >
          <p style={aboutText}>{g?.what ?? fm?.label ?? form}</p>
          {g?.not && (
            <p style={aboutText}>
              <strong style={{ fontWeight: 800 }}>Not this.</strong> {g.not}
            </p>
          )}
          {g?.already && (
            <p style={aboutText}>
              <strong style={{ fontWeight: 800 }}>Already done for you.</strong> {g.already}
            </p>
          )}
          {g?.noReply && (
            <p style={aboutText}>
              <strong style={{ fontWeight: 800 }}>Do not reply by email:</strong> {g.noReply}
            </p>
          )}
          {form === 'inquiry' && (
            <p style={aboutText}>
              <strong style={{ fontWeight: 800 }}>Topics.</strong> “Something else” also covers directions and
              website questions, and “Philanthropy &amp; giving” also covers partnership notes from the Contact
              page.
            </p>
          )}
          <p style={aboutText}>
            <strong style={{ fontWeight: 800 }}>On the public site.</strong>{' '}
            {g && g.from.length > 0 ? (
              <>
                “{g.asked}”, on{' '}
                {g.from.map((p, i) => (
                  <span key={p.path}>
                    {i > 0 && ' and '}
                    <a
                      href={siteLink(p.path)}
                      target="_blank"
                      rel="noreferrer"
                      className="hov-underline"
                      style={{
                        color: color.blue,
                        fontWeight: 700,
                        textDecoration: 'none',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 4,
                      }}
                    >
                      {p.label} <IconExternal size={12} />
                    </a>
                  </span>
                ))}
                .
              </>
            ) : (
              'Not offered yet.'
            )}
          </p>
        </div>
      )}
    </Card>
  );
}

const titleStyle: CSSProperties = {
  fontFamily: font.display,
  fontWeight: 600,
  fontSize: 19,
  color: color.navy,
  margin: 0,
};

const eyebrowStyle: CSSProperties = {
  fontSize: 11,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  fontWeight: 800,
  color: color.muted,
  margin: 0,
};

const aboutText: CSSProperties = { fontSize: 13, lineHeight: '19px', color: color.ink, margin: 0 };

function Label({ children }: { children: ReactNode }) {
  return <span style={{ color: color.muted, fontWeight: 700 }}>{children}</span>;
}

function Sep() {
  return (
    <span aria-hidden style={{ color: color.muted, margin: '0 9px' }}>
      ·
    </span>
  );
}
